import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePromo } from "@/context/PromoContext";
import { ProductCard } from "@/components/ProductCard";
import { Countdown } from "@/components/Countdown";
import { PatoisMarquee } from "@/components/PatoisMarquee";
import { EarlyAccess } from "@/components/EarlyAccess";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowDown } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/7d4b1824-6f94-4ced-ae91-4ba211ce6db0/images/cb1bfed40ab243ee1b223c4eded945c89fea94c82fc6723e602b903a40aa1d8b.jpeg";

const LOOKBOOK = [
  { img: "/brand/lookbook-rebel-by-design.webp", caption: "Jamaican by Birth · Rebel by Design", pos: "object-top" },
  { img: "/brand/lookbook-greatness-crew.webp", caption: "Greatness Is In My DNA · Crew" },
  { img: "https://customer-assets-wrfwihn1.emergentagent.net/job_patois-drop/artifacts/g5vptj5c_Screenshot_20260904-193717_Instagram.jpg", caption: "Rebel by Design" },
  { img: "https://customer-assets-wrfwihn1.emergentagent.net/job_patois-drop/artifacts/e6pqdzwt_Screenshot_20260904-193442_Instagram.jpg", caption: "In My DNA · 876" },
  { img: "https://static.prod-images.emergentagent.com/jobs/7d4b1824-6f94-4ced-ae91-4ba211ce6db0/images/44f2a22a39e8fd1d643869f2b2ee4054efa7476a4a147a713e358d0d7f569934.jpeg", caption: "Greatness · Green" },
  { img: "/brand/dna-woman-black.jpg", caption: "In My DNA" },
  { img: "https://static.prod-images.emergentagent.com/jobs/7d4b1824-6f94-4ced-ae91-4ba211ce6db0/images/4f7902e6257517f38d42443234b7b8e3f2c89eb7ab677af3a8dcd46231855467.jpeg", caption: "876 · Black" },
  { img: "https://static.prod-images.emergentagent.com/jobs/7d4b1824-6f94-4ced-ae91-4ba211ce6db0/images/038ab93ab79479cd786e835e457486d76a5e14e768e607a6cfc945bee8de0173.jpeg", caption: "876 · Gold" },
  { img: "/brand/greatness-black-back.jpg", caption: "Rooted to 876" },
  { img: "/brand/lion-rebel-black.jpg", caption: "Rebel by Design" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [drop, setDrop] = useState(null);
  const promo = usePromo();

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      setDrop(r.data.find((p) => p.limited_drop && p.drop_ends_at) || null);
    }).catch(() => {});
  }, []);

  const dropProducts = products.filter((p) => p.limited_drop);
  const featured = products.filter((p) => p.best_seller || p.limited_drop || p.featured);
  const grid = (featured.length ? featured : products).slice(0, 8);

  return (
    <div data-testid="home-page">
      {/* HERO — full-bleed lifestyle */}
      <section className="relative h-[92vh] min-h-[560px] w-full overflow-hidden">
        <img src={HERO_IMG} alt="Turahtid — worn on the streets of Jamaica" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 md:pb-24">
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-jamaica-gold mb-4 animate-fade-up">
            New Capsule · 876 Series
          </p>
          <h1 className="font-display text-6xl sm:text-8xl lg:text-[9rem] leading-[0.82] tracking-tight animate-fade-up" style={{ animationDelay: "80ms" }}>
            TURAH<span className="text-jamaica-gold">TID</span>
          </h1>
          {promo.active && (
          <div data-testid="home-promo" className="flex flex-wrap items-center gap-2 mt-4 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <span className="bg-jamaica-gold text-black font-mono text-[10px] sm:text-xs uppercase tracking-widest px-2.5 py-1">{promo.sale_percent}% Off Everything</span>
            <span className="bg-jamaica-green text-white font-mono text-[10px] sm:text-xs uppercase tracking-widest px-2.5 py-1">Free Shipping on {promo.free_ship_min_items}+ Items</span>
            {promo.ends_at && <Countdown endsAt={promo.ends_at} />}
          </div>
          )}
          <p className="text-zinc-200 mt-4 max-w-md text-sm sm:text-base animate-fade-up" style={{ animationDelay: "160ms" }}>
            Rooted to di soil. Real patois, printed heavy, worn loud — from Kingston to di world.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-8 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/shop?collection=876 Series" data-testid="hero-shop-btn">
              <Button className="px-10 py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none">
                Shop the Drop <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            {drop && <Countdown endsAt={drop.drop_ends_at} className="py-3" />}
          </div>
        </div>

        <div className="absolute bottom-6 right-6 z-10 hidden md:flex items-center gap-2 text-zinc-400 font-mono text-[10px] uppercase tracking-widest animate-bounce">
          Scroll <ArrowDown className="w-3 h-3" />
        </div>
      </section>

      <PatoisMarquee />

      {/* CAPSULE — editorial framing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-jamaica-green mb-3">The Capsule</p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.85]">
            876: ROOTED<br /><span className="text-jamaica-gold">TO THE SOIL</span>
          </h2>
          <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mt-6">
            876 is more than an area code — it's the heartbeat every yaadie carries abroad. This capsule
            prints the phrases raised us: the greetings from di corner shop, the wisdom from grandma
            verandah, the rebellion in di riddim. Each tee is a piece of home you can wear.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-12">
          {grid.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/shop" data-testid="view-all-btn">
            <Button variant="outline" className="px-10 py-6 border-border text-white hover:bg-secondary hover:border-jamaica-gold rounded-none uppercase tracking-widest font-bold">
              View Full Collection <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* LOOKBOOK — horizontal scroll, real settings */}
      <section className="py-12 md:py-16 border-y border-white/10 bg-card/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end justify-between mb-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-jamaica-green mb-2">Worn Inna Real Life</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight">DI LOOKBOOK</h2>
          </div>
          <span className="hidden sm:block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Swipe →</span>
        </div>
        <div data-testid="lookbook-scroll" className="flex gap-4 overflow-x-auto hide-scrollbar px-4 sm:px-6 lg:px-8 snap-x snap-mandatory">
          {LOOKBOOK.map((l, i) => (
            <div key={i} className="relative shrink-0 w-[75vw] sm:w-[340px] aspect-[3/4] overflow-hidden bg-zinc-900 border border-white/10 snap-start group">
              <img src={l.img} alt={l.caption} loading="lazy" data-testid={`lookbook-img-${i}`} className={`w-full h-full object-cover ${l.pos || ""} transition-transform duration-700 group-hover:scale-105`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-widest text-jamaica-gold">{l.caption}</span>
            </div>
          ))}
        </div>
      </section>

      {/* EARLY ACCESS — gated */}
      <EarlyAccess />

      {/* MOBILE STICKY SHOP NOW */}
      <Link
        to="/shop?collection=876 Series"
        data-testid="mobile-sticky-shop"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 pt-4 px-4 bg-jamaica-gold text-black font-bold uppercase tracking-widest text-sm text-center rounded-none shadow-2xl shadow-black/50 flex items-center justify-center gap-2"
      >
        Shop Now <ArrowRight className="w-4 h-4" />
      </Link>
      {/* spacer so sticky CTA never covers footer content on mobile */}
      <div className="h-24 md:hidden" aria-hidden />
    </div>
  );
}
